import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Button } from '../components/ui/button';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger 
} from '../components/ui/sheet';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Home,
  Plus,
  PieChart,
  CreditCard,
  BarChart3,
  Settings,
  Clock,
  Menu,
  Lock,
  Shield,
  User,
  Sun,
  Moon,
  Monitor
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { state, dispatch, lockLedger } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Add Transaction', href: '/add-transaction', icon: Plus },
    { name: 'Budget', href: '/budget', icon: PieChart },
    { name: 'Accounts', href: '/accounts', icon: CreditCard },
    { name: 'Schedules', href: '/schedules', icon: Clock },
    { name: 'Reports', href: '/reports', icon: BarChart3 },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const handleLock = () => {
    lockLedger();
    navigate('/login');
  };

  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    dispatch({ type: 'SET_THEME', payload: theme });
  };

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  const NavItems = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      {navigation.map((item, index) => {
        const Icon = item.icon;
        const current = isCurrentPath(item.href);
        
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={() => mobile && setIsMobileMenuOpen(false)}
            className={`nav-item flex items-center ${mobile ? 'mobile-nav-item' : 'px-4 py-3'} rounded-xl text-sm font-medium transition-all duration-300 animate-slide-in-right ${
              current
                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
                : 'text-muted-foreground hover:text-foreground hover:bg-primary/5 hover:shadow-md'
            } ${mobile ? 'touch-target' : ''}`}
            style={{ animationDelay: `${index * 100}ms` }}
            data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
          >
            <div className={`${mobile ? 'p-2.5' : 'p-2'} rounded-lg mr-3 transition-colors ${
              current ? 'bg-primary-foreground/10' : 'bg-transparent group-hover:bg-primary/10'
            }`}>
              <Icon className={`${mobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
            </div>
            <span className="flex-1 font-medium">{item.name}</span>
            {current && (
              <div className="w-2 h-2 bg-primary-foreground rounded-full animate-pulse" />
            )}
          </Link>
        );
      })}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-grow bg-card border-r border-border pt-5 overflow-y-auto shadow-soft">
          <div className="flex items-center flex-shrink-0 px-6 mb-8">
            <div className="p-2 bg-primary/10 rounded-xl mr-3">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold">PrivyBudget</h1>
              <p className="text-xs text-muted-foreground">The privacy-first budget app</p>
            </div>
          </div>
          
          <nav className="flex-grow px-4 space-y-2">
            <NavItems />
          </nav>

          {/* User Menu */}
          <div className="flex-shrink-0 p-4 border-t border-border/50">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-left p-3 rounded-xl hover:bg-primary/5 transition-all duration-200"
                  data-testid="user-menu-trigger"
                >
                  <div className="p-2 bg-primary/10 rounded-lg mr-3">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">
                      {state.auth.currentUser?.display || 'User'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {state.auth.household?.name || 'Household'}
                    </p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem onClick={() => handleThemeChange('light')}>
                  <Sun className="w-4 h-4 mr-2" />
                  Light Theme
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleThemeChange('dark')}>
                  <Moon className="w-4 h-4 mr-2" />
                  Dark Theme
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleThemeChange('system')}>
                  <Monitor className="w-4 h-4 mr-2" />
                  System Theme
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLock} data-testid="lock-ledger-button">
                  <Lock className="w-4 h-4 mr-2" />
                  Lock Ledger
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:pl-64 flex flex-col flex-1">
        {/* Mobile Header */}
        <div className="lg:hidden bg-card border-b border-border px-4 py-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="touch-target" data-testid="mobile-menu-trigger">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <div className="p-6 border-b border-border">
                  <SheetTitle className="flex items-center text-left">
                    <div className="p-2 bg-primary/10 rounded-lg mr-3">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold">PrivyBudget</h2>
                      <p className="text-xs text-muted-foreground">The privacy-first budget app</p>
                    </div>
                  </SheetTitle>
                </div>
                <nav className="p-4 space-y-2">
                  <NavItems mobile={true} />
                </nav>
                
                {/* Mobile User Menu */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-card">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start text-left p-4 rounded-xl hover:bg-primary/5 transition-all duration-200"
                      >
                        <div className="p-2 bg-primary/10 rounded-lg mr-3">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">
                            {state.auth.currentUser?.display || 'User'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {state.auth.household?.name || 'Household'}
                          </p>
                        </div>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      <DropdownMenuItem onClick={() => handleThemeChange('light')}>
                        <Sun className="w-4 h-4 mr-2" />
                        Light Theme
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleThemeChange('dark')}>
                        <Moon className="w-4 h-4 mr-2" />
                        Dark Theme
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleThemeChange('system')}>
                        <Monitor className="w-4 h-4 mr-2" />
                        System Theme
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLock}>
                        <Lock className="w-4 h-4 mr-2" />
                        Lock Ledger
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </SheetContent>
            </Sheet>
            
            <div className="ml-4">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-lg mr-3">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="font-bold text-base">PrivyBudget</span>
                  <p className="text-xs text-muted-foreground hidden sm:block">The privacy-first budget app</p>
                </div>
              </div>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="touch-target" data-testid="mobile-user-menu">
                <User className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleThemeChange('light')}>
                <Sun className="w-4 h-4 mr-2" />
                Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleThemeChange('dark')}>
                <Moon className="w-4 h-4 mr-2" />
                Dark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleThemeChange('system')}>
                <Monitor className="w-4 h-4 mr-2" />
                System
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLock}>
                <Lock className="w-4 h-4 mr-2" />
                Lock
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Main Content */}
        <main className="flex-1 pb-4 sm:pb-8">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>

      {/* Security Indicator */}
      <div className="fixed bottom-4 right-4 z-50">
        <div className="flex items-center space-x-2 bg-card border border-border rounded-full px-4 py-2 shadow-soft backdrop-blur-sm">
          <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-xs text-foreground font-semibold">End-to-End Encrypted</span>
        </div>
      </div>
    </div>
  );
}